/**
 * SessionGate — renders the app only once the server has said who you are and what you may do.
 *
 * The states it exists to keep apart:
 *   · signed in, NO Stride role → a page that says so and names who to ask. Not a redirect: the
 *     redirect answer to "you have no role" is an endless loop through a sign-in that keeps
 *     succeeding and keeps granting nothing.
 *   · the server can't authenticate anyone (auth unconfigured) → say THAT, because no amount of
 *     signing in will fix it and the person needs to escalate rather than retry.
 *   · a dev bypass is in force → a persistent banner. An unsecured API that looks identical to a
 *     secured one is how "we'll add auth later" becomes "we shipped without auth".
 */
import type { ReactNode } from 'react'
import { ExclamationTriangleIcon, LockClosedIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline'
import { useSession } from '../../context/SessionContext'
import { useAuth } from '../../hooks/useAuth'

function Centered({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-6 text-fg">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

/** The bypass banner. Deliberately hard to miss and not dismissible. */
export function DevBypassBanner(): JSX.Element | null {
  const { devBypass } = useSession()
  if (!devBypass) return null
  return (
    <div
      data-testid="dev-bypass-banner"
      className="flex items-center justify-center gap-2 bg-warn-soft px-3 py-1 text-[11px] font-semibold text-warn"
    >
      <ShieldExclamationIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      Authentication is bypassed — this session is a local development admin, not a signed-in user.
    </div>
  )
}

export function SessionGate({ children }: { children: ReactNode }): JSX.Element {
  const { state, me, reason, refresh } = useSession()
  const { account, logout } = useAuth()

  if (state === 'loading') {
    return (
      <Centered>
        <p data-testid="session-loading" className="text-center text-sm text-fg-muted">Checking your access…</p>
      </Centered>
    )
  }

  /**
   * Signed in, but no project — which is now the normal "not invited yet" answer.
   *
   * The server no longer refuses these callers: it cannot, because invitations are claimed by `/api/me`, and
   * a gate that 403'd an uninvited user would refuse them before the claim could run. So the absence of
   * access shows up here as an empty project list rather than as an error, and this screen has to explain it.
   */
  const signedInWithNothing = state === 'ready'
    && (me?.projects.length ?? 0) === 0
    // NOT when the list merely failed to load. Those look identical on the wire and mean opposite things —
    // telling an invited person they were never invited because storage hiccuped is the worst version of
    // this screen.
    && me?.projectsUnavailable !== true
    // …and not when they administer the install. A super admin on a fresh deployment legitimately has no
    // projects — there are none yet — and they are the one person who can create the first. Locking them out
    // of the UI that does it would make a new install unusable.
    && me?.can.administer !== true

  if (state === 'no-role' || signedInWithNothing) {
    /**
     * The address we matched on — the single most useful thing on this screen.
     *
     * An invitation is matched by email, and the address an admin types is often not the one the token
     * carries (`p.sharma@corp.example` versus `priya.sharma@example.com` is ordinary Entra configuration).
     * When that happens both parties are certain the invitation should have worked. Showing the identity
     * Stride actually saw turns a mystery into a comparison anyone can make in five seconds.
     */
    const identity = me?.user.upn ?? account?.username

    return (
      <Centered>
        <div data-testid="session-no-role" className="rounded-card border border-border bg-surface p-6 shadow-card">
          <LockClosedIcon className="h-6 w-6 text-warn" aria-hidden />
          <h1 className="mt-3 text-base font-semibold text-fg">You don’t have access to Stride yet</h1>
          <p className="mt-1.5 text-[13px] leading-relaxed text-fg-muted">
            Access to Stride is by invitation. Ask an administrator to invite you — they will need the exact
            address below, and access begins the next time you sign in.
          </p>
          {identity !== undefined && (
            <div className="mt-3 rounded-md border border-border bg-surface-2 px-3 py-2" data-testid="session-identity">
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-subtle">
                Invite this exact address
              </p>
              <p className="mt-0.5 select-all font-mono text-[12px] text-fg">{identity}</p>
              <p className="mt-1 text-[11px] leading-snug text-fg-subtle">
                If you were invited under a different address, that is why this is empty — an invitation is
                matched to the account you sign in with.
              </p>
            </div>
          )}
          {reason && (
            <p data-testid="session-no-role-reason" className="mt-2 rounded-md bg-surface-2 px-3 py-2 font-mono text-[11px] leading-snug text-fg-subtle">
              {reason}
            </p>
          )}
          <div className="mt-4 flex items-center gap-2">
            {/* Re-check rather than reload: an admin can grant the role while this page is open. */}
            <button
              type="button" data-testid="session-recheck" onClick={refresh}
              className="rounded-control border border-accent bg-accent px-3 py-1.5 text-xs font-semibold text-accent-fg hover:bg-accent-600"
            >Check again</button>
            <button
              type="button" data-testid="session-signout" onClick={() => logout()}
              className="rounded-control border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-fg-muted hover:bg-surface-2"
            >Sign out</button>
          </div>
        </div>
      </Centered>
    )
  }

  if (state === 'unavailable') {
    return (
      <Centered>
        <div data-testid="session-unavailable" className="rounded-card border border-del-line bg-surface p-6 shadow-card">
          <ExclamationTriangleIcon className="h-6 w-6 text-del" aria-hidden />
          <h1 className="mt-3 text-base font-semibold text-fg">Stride can’t verify who you are</h1>
          <p className="mt-1.5 text-[13px] leading-relaxed text-fg-muted">
            The server couldn’t establish your identity. Signing in again won’t help — this needs an
            administrator to look at the server’s authentication configuration.
          </p>
          {reason && (
            <p data-testid="session-unavailable-reason" className="mt-2 rounded-md bg-surface-2 px-3 py-2 font-mono text-[11px] leading-snug text-fg-subtle">
              {reason}
            </p>
          )}
          <button
            type="button" data-testid="session-retry" onClick={refresh}
            className="mt-4 rounded-control border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-fg-muted hover:bg-surface-2"
          >Retry</button>
        </div>
      </Centered>
    )
  }

  // state === 'ready'
  return (
    <>
      <DevBypassBanner />
      {me != null && me.can.read && !me.can.deploy && (
        // Someone who cannot deploy will find those controls refused by the server. Saying so up front beats
        // discovering it one 403 at a time.
        //
        // Driven by `can`, which the API resolves from the ACTIVE PROJECT — not by `user.role`, which is the
        // legacy global role resolved against the `default` project. A project-admin who does not also belong
        // to `default` came back `viewer` there, so this banner told them deployments were disabled while
        // they held every permission in their own project. They believed it and stopped, which is worse than
        // a 403: at least a refusal can be argued with.
        <div data-testid="viewer-banner" className="flex items-center justify-center gap-2 bg-surface-2 px-3 py-1 text-[11px] text-fg-muted">
          Read-only access in this project — deploying and promoting are not among your permissions.
        </div>
      )}
      {children}
    </>
  )
}
